import axios from 'axios';
import api from '../config/axios'; // Tu instancia configurada de Axios
import API_ROUTES from '../config/apiRoutes'; // Tu archivo de rutas

// --- Importamos los tipos necesarios ---
import { ApiResponse } from '../interfaces/api.types';
import { 
    ClienteCaso, 
    FullCaseDetails, 
    NewCaseFormData, 
    EstadoCaso 
} from '../interfaces/cliente.types';

/**
 * @function handleApiError
 * @description Función de ayuda para manejar errores de Axios de forma consistente.
 * @param {unknown} error - El error capturado en el bloque catch.
 * @returns {never}
 */
const handleApiError = (error: unknown): never => {
    if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Ocurrió un error en el servidor.');
    }
    throw new Error('Ocurrió un error de conexión o un problema desconocido.');
};

/**
 * @function getAllCasesByStatus
 * @description Obtiene una lista de casos filtrada por su estado.
 * @param {EstadoCaso} [estado='Activo'] - El estado por el cual filtrar.
 * @returns {Promise<ClienteCaso[]>} Un array de los expedientes encontrados.
 */
export const getAllCasesByStatus = async (estado: EstadoCaso = 'Activo'): Promise<ClienteCaso[]> => {
    try {
        const response = await api.get<ApiResponse<ClienteCaso[]>>(API_ROUTES.clientes.listByStatus(estado));
        return response.data.data || [];
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function searchCases
 * @description Busca expedientes que coincidan con un término de búsqueda.
 * @param {string} term - El término a buscar (nombre, DNI, o N° de expediente).
 * @returns {Promise<ClienteCaso[]>} Un array con los resultados de la búsqueda.
 */
export const searchCases = async (term: string): Promise<ClienteCaso[]> => {
    if (term.length < 3) {
        return []; // No buscar si el término es muy corto
    }
    try {
        const response = await api.get<ApiResponse<ClienteCaso[]>>(API_ROUTES.clientes.search(term));
        return response.data.data || [];
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function getCaseDetails
 * @description Obtiene el "Dossier" completo de un caso, incluyendo pagos y audiencias.
 * @param {number} caseId - El ID del expediente.
 * @returns {Promise<FullCaseDetails>} El objeto completo del caso.
 */
export const getCaseDetails = async (caseId: number): Promise<FullCaseDetails> => {
    try {
        const response = await api.get<ApiResponse<FullCaseDetails>>(API_ROUTES.clientes.getDetails(caseId));
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo obtener el detalle del caso.");
        }
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function createCase
 * @description Crea un nuevo expediente (cliente/caso).
 * @param {NewCaseFormData} caseData - Los datos del formulario para el nuevo caso.
 * @returns {Promise<ClienteCaso>} El objeto del expediente recién creado.
 */
export const createCase = async (caseData: NewCaseFormData): Promise<ClienteCaso> => {
    try {
        const response = await api.post<ApiResponse<ClienteCaso>>(API_ROUTES.clientes.create, caseData);
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo crear el expediente.");
        }
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function updateCaseStatus
 * @description Actualiza el estado de un caso (ej. de 'Activo' a 'Cerrado').
 * @param {number} caseId - El ID del expediente.
 * @param {EstadoCaso} nuevoEstado - El nuevo estado para el caso.
 * @param {string} [motivoCierre] - Motivo opcional si el estado es 'Cerrado'.
 * @returns {Promise<string>} Un mensaje de confirmación.
 */
export const updateCaseStatus = async (caseId: number, nuevoEstado: EstadoCaso, motivoCierre?: string): Promise<string> => {
    try {
        const payload = { nuevoEstado, motivoCierre };
        const response = await api.post<ApiResponse<null>>(API_ROUTES.clientes.updateStatus(caseId), payload);
        return response.data.message || 'Estado actualizado correctamente.';
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function sendSummaryEmail
 * @description Solicita al backend que envíe un resumen del caso al correo del cliente.
 * @param {number} caseId - El ID del expediente.
 * @returns {Promise<string>} Un mensaje de confirmación.
 */
export const sendSummaryEmail = async (caseId: number): Promise<string> => {
    try {
        const response = await api.post<ApiResponse<null>>(API_ROUTES.clientes.sendSummaryEmail(caseId));
        return response.data.message || 'Resumen enviado exitosamente.';
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function deleteCase
 * @description Elimina un expediente completo (incluyendo sus pagos y audiencias asociados).
 * @param {number} caseId - El ID del expediente a eliminar.
 * @returns {Promise<string>} Un mensaje de confirmación.
 */
export const deleteCase = async (caseId: number): Promise<string> => {
    try {
        const response = await api.delete<ApiResponse<null>>(API_ROUTES.clientes.delete(caseId));
        return response.data.message || 'Expediente eliminado exitosamente.';
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function updateCase
 * @description ¡NUEVA FUNCIÓN! Actualiza los datos de un expediente existente.
 * @param {number} caseId - El ID del expediente a actualizar.
 * @param {Partial<NewCaseFormData>} caseData - Los datos a modificar.
 * @returns {Promise<ClienteCaso>} El objeto del expediente actualizado.
 */
export const updateCase = async (caseId: number, caseData: Partial<NewCaseFormData>): Promise<ClienteCaso> => {
    try {
        const response = await api.put<ApiResponse<ClienteCaso>>(API_ROUTES.clientes.update(caseId), caseData);
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo actualizar el expediente.");
        }
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};